const PROPOSAL_SECTION_LABELS = {
  executiveSummary: 'Executive Summary',
  understandingNeeds: 'Understanding Your Needs',
  proposedSolution: 'Proposed Solution',
  proposedDeliverables: 'Proposed Deliverables',
  whyStratel: 'Why Stratel',
  investment: 'Investment',
  acceptance: 'Acceptance',
};

const INTERNAL_ONLY_SECTION_KEYS = new Set([
  'documentControl',
]);

const INTERNAL_ONLY_SECTION_TITLES = new Set([
  'document control',
]);

const TABLE_SCHEMAS = {
  timeline: ['Phase', 'Duration', 'Details'],
  proposalNeeds: ['#', 'Challenge'],
  proposalDeliverables: ['#', 'Deliverable', 'Timeline', 'Format'],
  proposalInvestment: ['#', 'Service / Component', 'Fee / Rate', 'Notes'],
  scopeDeliverables: ['#', 'Deliverable / Service', 'Description', 'Due Date', 'Format'],
  feesPaymentSchedule: ['#', 'Payment Trigger / Milestone', 'Amount Due ($)', 'Due Date'],
  sowTimeline: ['#', 'Milestone', 'Deliverable(s)', 'Target Date', 'Status', 'Owner'],
  courseModules: ['Module', 'Base', 'Add-Ons', 'Format', 'Topic/Content Areas', 'Est. Dev Time', 'Price'],
};

const TABLE_SCHEMA_ALIASES = {
  timeline: 'timeline',
  'timeline milestones': 'sowTimeline',
  'timeline & milestones': 'sowTimeline',
  'understanding your needs': 'proposalNeeds',
  'proposed deliverables': 'proposalDeliverables',
  investment: 'proposalInvestment',
  'scope of services deliverables': 'scopeDeliverables',
  'scope of services & deliverables': 'scopeDeliverables',
  'scope deliverables': 'scopeDeliverables',
  'payment schedule': 'feesPaymentSchedule',
  'fees payment schedule': 'feesPaymentSchedule',
  'fees payment': 'feesPaymentSchedule',
  'course modules deliverables': 'courseModules',
  'course modules & deliverables': 'courseModules',
};

const normalizeToken = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const splitPipeRow = (line = '') =>
  String(line)
    .split('|')
    .map((cell) => cell.trim())
    .filter((cell, index, cells) => !(cells.length > 1 && cell === '' && (index === 0 || index === cells.length - 1)));

const looksLikePipeRow = (line = '') => {
  const trimmed = String(line).trim();
  if (!trimmed || !trimmed.includes('|')) return false;
  if (/^[-*•]\s/.test(trimmed)) return false;
  return splitPipeRow(trimmed).length > 1;
};

const getTableSchema = (sectionKey = '', context = {}) => {
  if (sectionKey === 'timeline' && context.docType === 'sow') return TABLE_SCHEMAS.sowTimeline;
  if (sectionKey === 'feesPayment') return TABLE_SCHEMAS.feesPaymentSchedule;
  return TABLE_SCHEMAS[sectionKey] || null;
};

const firstTableLabel = (text = '') => {
  const match = String(text).match(/^(.*?):\s*$/);
  return match ? match[1] : '';
};

const inferSchemaFromLabel = (label = '', context = {}) => {
  const normalized = normalizeToken(label).replace(/\s+/g, ' ');
  const directKey = TABLE_SCHEMA_ALIASES[normalized];
  if (!directKey) return null;
  if (directKey === 'timeline' && context.docType === 'sow') return TABLE_SCHEMAS.sowTimeline;
  return TABLE_SCHEMAS[directKey] || null;
};

const inferSchemaFromHeaders = (rows = [], context = {}) => {
  if (!rows.length) return null;
  const headerRow = rows[0];
  if (!headerRow?.length) return null;

  const candidateOrder = [
    context.docType === 'sow' ? 'sowTimeline' : 'timeline',
    'feesPaymentSchedule',
    'scopeDeliverables',
    'courseModules',
    context.docType !== 'sow' ? 'sowTimeline' : 'timeline',
  ];

  for (const key of candidateOrder) {
    const schema = TABLE_SCHEMAS[key];
    if (schema && rowsMatchHeader(headerRow, schema)) return schema;
  }
  return null;
};

const rowsMatchHeader = (row = [], header = []) =>
  row.length === header.length &&
  row.every((cell, index) => normalizeToken(cell) === normalizeToken(header[index]));

const flushTextBlock = (blocks, textLines) => {
  const content = textLines.join('\n').trim();
  if (content) blocks.push({ type: 'text', content });
  textLines.length = 0;
};

const getClientFacingContent = (content = '', sectionKey = '', context = {}) => {
  return String(content || '');
};

export function isClientVisibleSection(section = {}) {
  const key = String(section?.key || '').trim();
  const title = normalizeToken(section?.label || section?.title || '');
  if (INTERNAL_ONLY_SECTION_KEYS.has(key)) return false;
  if (INTERNAL_ONLY_SECTION_TITLES.has(title)) return false;
  return true;
}

export function filterClientFacingSections(sections = []) {
  return (sections || []).filter((section) => isClientVisibleSection(section));
}

export function parseStructuredContent(content = '', sectionKey = '', context = {}) {
  const lines = getClientFacingContent(content, sectionKey, context).split('\n');
  const blocks = [];
  const textLines = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (!looksLikePipeRow(line)) {
      textLines.push(line);
      continue;
    }

    const rowLines = [];
    let cursor = index;
    while (cursor < lines.length && looksLikePipeRow(lines[cursor])) {
      rowLines.push(lines[cursor]);
      cursor += 1;
    }

    const rows = rowLines.map(splitPipeRow).filter((row) => row.length > 1);
    if (!rows.length) {
      textLines.push(...rowLines);
      index = cursor - 1;
      continue;
    }

    const preceding = blocks[blocks.length - 1];
    const label = preceding?.type === 'text' ? firstTableLabel(preceding.content.split('\n').slice(-1)[0]) : '';
    const schema = getTableSchema(sectionKey, context)
      || inferSchemaFromLabel(label, context)
      || inferSchemaFromHeaders(rows, context);

    if (!schema && rows.length < 2) {
      textLines.push(...rowLines);
      index = cursor - 1;
      continue;
    }

    flushTextBlock(blocks, textLines);

    let headers = schema || rows[0];
    let dataRows = rows;
    if (rowsMatchHeader(rows[0], headers)) {
      dataRows = rows.slice(1);
    } else if (!schema) {
      dataRows = rows.slice(1);
    }

    dataRows = dataRows.filter((row) => row.some((cell) => String(cell || '').trim()));
    if (dataRows.length) {
      blocks.push({
        type: 'table',
        headers,
        rows: dataRows,
        label,
      });
    }

    index = cursor - 1;
  }

  flushTextBlock(blocks, textLines);
  return blocks;
}

export function normalizeProposalPreviewSections(sections = []) {
  const byKey = new Map((sections || []).map((section) => [section.key, section]));
  const normalized = [];

  const push = (key, content, label) => {
    const trimmed = String(content || '').trim();
    if (!trimmed) return;
    normalized.push({ key, label: label || PROPOSAL_SECTION_LABELS[key] || key, content: trimmed });
  };

  ['executiveSummary', 'understandingNeeds', 'proposedSolution', 'proposedDeliverables', 'whyStratel', 'investment', 'acceptance'].forEach((key) => {
    const existing = byKey.get(key);
    if (existing?.content) normalized.push(existing);
  });

  (sections || [])
    .filter((section) => ![
      'executiveSummary',
      'understandingNeeds',
      'proposedSolution',
      'proposedDeliverables',
      'whyStratel',
      'investment',
      'acceptance',
    ].includes(section.key))
    .forEach((section) => normalized.push(section));

  return normalized;
}

export function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function textBlockToHtml(text = '') {
  const paragraphs = String(text || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return paragraphs
    .map((paragraph) => {
      const lines = paragraph.split('\n').map((line) => line.trimEnd());
      if (lines.every((line) => /^[-*•]\s/.test(line))) {
        return `<ul>${lines.map((line) => `<li>${escapeHtml(line.replace(/^[-*•]\s/, ''))}</li>`).join('')}</ul>`;
      }
      if (lines.length === 1 && /^###\s/.test(lines[0])) return `<h3>${escapeHtml(lines[0].replace(/^###\s/, ''))}</h3>`;
      if (lines.length === 1 && /^##\s/.test(lines[0])) return `<h2>${escapeHtml(lines[0].replace(/^##\s/, ''))}</h2>`;
      if (lines.length === 1 && /^#\s/.test(lines[0])) return `<h1>${escapeHtml(lines[0].replace(/^#\s/, ''))}</h1>`;

      const body = escapeHtml(lines.join('\n'))
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br/>');
      return `<p>${body}</p>`;
    })
    .join('');
}

export function structuredContentToHtml(content = '', sectionKey = '', context = {}) {
  const blocks = parseStructuredContent(content, sectionKey, context);
  return blocks
    .map((block) => {
      if (block.type === 'table') {
        const headerHtml = block.headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('');
        const bodyHtml = block.rows
          .map((row) => `<tr>${block.headers.map((_, index) => `<td>${escapeHtml(row[index] || '')}</td>`).join('')}</tr>`)
          .join('');
        return `
          <div class="contract-table-wrap">
            <table class="contract-table">
              <thead><tr>${headerHtml}</tr></thead>
              <tbody>${bodyHtml}</tbody>
            </table>
          </div>
        `;
      }
      return textBlockToHtml(block.content);
    })
    .join('');
}
